import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { UseCaseData, UseCaseFormData } from "../../types/use-case.types"
import type { UseMutationResult } from "@tanstack/react-query"

import {
  ClipboardCheck,
  Download,
  FileUp,
  HelpCircle,
  Presentation,
  Printer,
  RotateCcw,
  Save,
  Share2,
  Terminal,
  Trash2,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { SettingsService } from "@/services/settings-service"

import { AIResultDialog } from "./ai-result-dialog"
import { FactSheetGuideDialog } from "./fact-sheet-guide-dialog"
import { UseCaseShareDialog } from "./use-case-share-dialog"
import { useAiClaudePrompt } from "../../hooks/use-ai-claude-prompt"

type FactSheetActionsProps = {
  isEditing: boolean
  isSaving: boolean
  onSave: () => void
  onDelete: () => void
  onReset: () => void
  onImport: (data: Record<string, unknown>) => void
  onExport: () => Record<string, unknown>
  ucName: string
  currentUserRole?: string
  useCaseId?: string
  useCase?: UseCaseData
  getFormData?: () => UseCaseFormData
  // AI features (managed by parent)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  qaReview?: UseMutationResult<any, any, any, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  salesPitch?: UseMutationResult<any, any, any, any>
  onRunQa?: () => void
  onRunSalesPitch?: () => void
}

export function FactSheetActions({
  isEditing,
  isSaving,
  onSave,
  onDelete,
  onReset,
  onImport,
  onExport,
  ucName,
  currentUserRole,
  useCaseId,
  useCase,
  getFormData,
  qaReview,
  salesPitch,
  onRunQa,
  onRunSalesPitch,
}: FactSheetActionsProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)

  // Claude Prompt still uses its own dialog (not an inline card) — admin only
  const isUseCaseAdmin = SettingsService.getInstance().settings.user_policy.is_usecase_admin
  const [claudePromptDialogOpen, setClaudePromptDialogOpen] = useState(false)
  const claudePrompt = useAiClaudePrompt()

  const triggerClaudePrompt = () => {
    if (!getFormData) return
    setClaudePromptDialogOpen(true)
    claudePrompt.mutate(getFormData())
  }

  const isOwner = currentUserRole === "owner"

  const handlePrint = () => {
    window.print()
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string)
        onImport(data)
      } catch {
        // Invalid JSON - ignore
      }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const handleExport = () => {
    const data = onExport()
    const json = JSON.stringify(data, null, 2)
    const blob = new Blob([json], { type: "text/plain" })
    const filename = (ucName || "use_case").replace(/[^a-z0-9]/gi, "_") + "_fact_sheet.txt"
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false)
    onDelete()
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={isSaving} size="sm" onClick={onSave}>
          <Save className="mr-1 size-4" />
          {isSaving ? t("useCases.factSheet.actions.saving") : t("useCases.factSheet.actions.save")}
        </Button>
        {isOwner && useCaseId && (
          <Button size="sm" variant="outline" onClick={() => setShareDialogOpen(true)}>
            <Share2 className="mr-1 size-4" />
            Share
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={handlePrint}>
          <Printer className="mr-1 size-4" />
          {t("useCases.factSheet.actions.print")}
        </Button>
        <Button size="sm" variant="outline" onClick={handleImportClick}>
          <FileUp className="mr-1 size-4" />
          {t("useCases.factSheet.actions.importTxt")}
        </Button>
        <Button size="sm" variant="outline" onClick={handleExport}>
          <Download className="mr-1 size-4" />
          {t("useCases.factSheet.actions.exportTxt")}
        </Button>
        {isEditing && isOwner && (
          <Button size="sm" variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="mr-1 size-4" />
            {t("useCases.factSheet.actions.delete")}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onReset}>
          <RotateCcw className="mr-1 size-4" />
          {t("useCases.factSheet.actions.reset")}
        </Button>

        {getFormData && (
          <>
            <div className="bg-border mx-1 h-6 w-px" />
            {onRunQa && (
              <Button disabled={qaReview?.isPending} size="sm" variant="outline" onClick={onRunQa}>
                <ClipboardCheck className="mr-1 size-4" />
                QA Check
              </Button>
            )}
            {onRunSalesPitch && (
              <Button
                disabled={salesPitch?.isPending}
                size="sm"
                variant="outline"
                onClick={onRunSalesPitch}
              >
                <Presentation className="mr-1 size-4" />
                Sales Pitch
              </Button>
            )}
            {isUseCaseAdmin && (
              <Button size="sm" variant="outline" onClick={triggerClaudePrompt}>
                <Terminal className="mr-1 size-4" />
                Claude Prompt
              </Button>
            )}
          </>
        )}

        <div className="ml-auto">
          <Button size="sm" variant="ghost" onClick={() => setGuideOpen(true)}>
            <HelpCircle className="mr-1 size-4" />
            Guide
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        accept=".txt,.json"
        className="hidden"
        type="file"
        onChange={handleFileChange}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("useCases.factSheet.actions.delete")}</DialogTitle>
            <DialogDescription>{t("useCases.factSheet.actions.deleteConfirm")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              {t("useCases.factSheet.actions.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {useCaseId && (
        <UseCaseShareDialog
          open={shareDialogOpen}
          useCase={useCase}
          useCaseId={useCaseId}
          onOpenChange={setShareDialogOpen}
        />
      )}

      {/* Claude Prompt dialog (only this one remains as a dialog) */}
      <AIResultDialog
        isLoading={claudePrompt.isPending}
        open={claudePromptDialogOpen}
        result={claudePrompt.data?.text ?? null}
        title="Claude Prompt"
        onOpenChange={setClaudePromptDialogOpen}
        onRegenerate={triggerClaudePrompt}
      />

      {/* Guide dialog */}
      <FactSheetGuideDialog open={guideOpen} onOpenChange={setGuideOpen} />
    </>
  )
}
